import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  ServerIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ServerStackIcon,
  CloudArrowUpIcon,
  ShoppingCartIcon,
  GlobeAltIcon,
  ChevronUpDownIcon,
  QueueListIcon,
  SwatchIcon,
  BellAlertIcon,
  KeyIcon,
  CubeIcon,
  FolderIcon,
  UserGroupIcon,
  PencilSquareIcon,
  MegaphoneIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  Squares2X2Icon,
  AdjustmentsHorizontalIcon,
  BanknotesIcon,
  CurrencyEuroIcon,
  IdentificationIcon,
  MicrophoneIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore, OrgWithPermissions } from '@/stores/organization';
import { useEcommerceStore } from '@/stores/ecommerce';
import { PERMISSIONS } from '@/types';
import { organizationsApi } from '@/services/api';
import pkg from '../../../package.json';

// Version del panel, del package.json. En el pie del sidebar en gris claro
// para ver de un vistazo si el panel abierto esta desactualizado. Vite
// resuelve el JSON de forma nativa (mas fiable que define, que aqui no
// llegaba a sustituir la variable).
const APP_VERSION = pkg.version;


type NavGroupId = 'webs' | 'crm' | 'gestion' | 'ajustes' | 'sistemas';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  permissions?: string[];
  // Permiso de plataforma (VIDA-347) que también da acceso al item, aunque la
  // org seleccionada no lo conceda: el «Equipo técnico VIDA» no es miembro.
  platform?: string;
  // Items sharing a group id render under a collapsible group header.
  // Items in the source array MUST keep their group's members contiguous.
  group?: NavGroupId;
}

interface SidebarProps {
  onClose?: () => void;
}

// Collapsible group config. `flattenIfSingle`: if only one child is visible,
// show it as a normal top-level item (no group header).
const NAV_GROUPS: Record<NavGroupId, {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  flattenIfSingle: boolean;
}> = {
  webs: { label: 'Webs', icon: GlobeAltIcon, flattenIfSingle: true },
  // CRM: contactos multi-fuente, empresas, propuestas. Opt-in por org.
  crm: { label: 'CRM', icon: UserGroupIcon, flattenIfSingle: false },
  // Gestion: nucleo administrativo de la organizacion (presupuesto, cuotas,
  // asociados). Justo encima de Ajustes; debajo de los modulos operativos.
  gestion: { label: 'Gestión', icon: BanknotesIcon, flattenIfSingle: false },
  ajustes: { label: 'Ajustes', icon: AdjustmentsHorizontalIcon, flattenIfSingle: false },
  sistemas: { label: 'Sistemas', icon: ServerStackIcon, flattenIfSingle: false },
};

// IMPORTANT: keep each group's members contiguous in this array — the render
// collapses consecutive items sharing the same `group` into one group header.
// Order: 'ajustes' then 'sistemas' go LAST, so Ajustes sits just above Sistemas
// (and at the very bottom when the user has no Sistemas access).
const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },

  // --- Grupo "CRM" (opt-in por org: config.management.crm.enabled). Va arriba,
  //     donde estaria "Clientes"; cuando el CRM esta activo, "Contactos" es la
  //     lista unificada de clientes (incluye los de ecommerce) y el "Clientes"
  //     de ecommerce de abajo se oculta. ---
  {
    name: 'Contactos',
    href: '/crm/contacts',
    icon: UserGroupIcon,
    permissions: [PERMISSIONS.CRM_READ],
    group: 'crm',
  },
  {
    // Con CRM activo vive dentro del grupo, bajo Contactos: una solicitud de
    // colaborador es la entrada de un tercero a la red y encaja en ese embudo.
    // Sin CRM aparece suelta bajo Clientes (ver el gemelo mas abajo).
    name: 'Colaboradores',
    href: '/colaboradores',
    icon: UserGroupIcon,
    // Basta con poder ver las fichas O las solicitudes para que aparezca el menú.
    permissions: [PERMISSIONS.WEAVERS_READ, PERMISSIONS.COLLABORATORS_READ],
    group: 'crm',
  },
  {
    name: 'Pipeline',
    href: '/crm/pipeline',
    icon: Squares2X2Icon,
    permissions: [PERMISSIONS.CRM_READ],
    group: 'crm',
  },

  {
    name: 'Clientes',
    href: '/customers',
    icon: UserGroupIcon,
    permissions: [PERMISSIONS.ECOMMERCE_READ],
  },
  {
    // Gemelo del anterior para cuando la organizacion NO tiene CRM: entonces no
    // hay grupo donde meterlo y se muestra suelto, bajo Clientes. Solo uno de
    // los dos se pinta nunca; el filtro de abajo se encarga.
    name: 'Colaboradores',
    href: '/colaboradores',
    icon: UserGroupIcon,
    permissions: [PERMISSIONS.WEAVERS_READ, PERMISSIONS.COLLABORATORS_READ],
  },
  {
    name: 'Productos',
    href: '/products',
    icon: CubeIcon,
    permissions: [PERMISSIONS.PRODUCTS_READ],
  },

  // --- Grupo "Webs" (se aplana si solo hay una visible) ---
  {
    name: 'PET-VIDA',
    href: '/pet',
    icon: GlobeAltIcon,
    // CMS: cms:read (nuevo) o content:read (alias legacy).
    permissions: [PERMISSIONS.CMS_READ, PERMISSIONS.CONTENT_READ],
    group: 'webs',
  },
  {
    name: 'Blog',
    href: '/blog',
    icon: PencilSquareIcon,
    permissions: [PERMISSIONS.BLOG_READ],
    group: 'webs',
  },
  {
    name: 'Ecommerce',
    href: '/ecommerce',
    icon: ShoppingCartIcon,
    permissions: [PERMISSIONS.ECOMMERCE_READ],
    group: 'webs',
  },
  {
    name: 'Catalogo',
    href: '/catalog',
    icon: SwatchIcon,
    permissions: [PERMISSIONS.CATALOG_READ],
    group: 'webs',
  },

  {
    name: 'Proyectos',
    href: '/projects',
    icon: FolderIcon,
    permissions: [PERMISSIONS.PROJECTS_READ],
  },
  {
    name: 'Marketing',
    href: '/marketing',
    icon: MegaphoneIcon,
    // Pixeles de tracking, generador de UTM e informe de campañas.
    permissions: [PERMISSIONS.MARKETING_READ],
  },
  {
    name: 'Planificador',
    href: '/planner',
    icon: CalendarDaysIcon,
    // Recursos y servicios (Módulo A). La página avisa si el org no tiene
    // features.planner activo.
    permissions: [PERMISSIONS.PLANNER_READ],
  },
  {
    name: 'Procesos',
    href: '/processes',
    icon: Squares2X2Icon,
    // Definición de procesos (Módulo B). La página avisa si el org no tiene
    // features.processes activo.
    permissions: [PERMISSIONS.PROCESSES_READ],
  },
  {
    name: 'Metadatos',
    href: '/metadata',
    icon: QueueListIcon,
    // El arbol de metadatos es administrativo — antes pedia CONTENT_READ
    // (lo tienen casi todos los roles editoriales). Ahora METADATA_ADMIN
    // para que solo lo vean admins reales (super_admin via *, o quien
    // tenga el permiso asignado explicitamente).
    permissions: [PERMISSIONS.METADATA_ADMIN],
  },
  // --- Grupo "Gestion" (nucleo administrativo de la asociacion) ---
  // Sin permissions → visibles a todos los autenticados mientras los modulos
  // El backend YA exige estos permisos (management.routes.ts). Sin declararlos
  // aqui, el menu de Gestion se enseñaba a CUALQUIER usuario —aunque el backend
  // devolviera 403 al entrar—: un gestor de catalogo veia Presupuesto, Cuotas y
  // Asociados. Cada item se restringe a su permiso de lectura.
  {
    name: 'Presupuesto',
    href: '/budget',
    icon: BanknotesIcon,
    permissions: [PERMISSIONS.BUDGET_READ],
    group: 'gestion',
  },
  {
    name: 'Cuotas',
    href: '/fees',
    icon: CurrencyEuroIcon,
    permissions: [PERMISSIONS.FEES_READ],
    group: 'gestion',
  },
  {
    name: 'Asociados',
    href: '/members',
    icon: IdentificationIcon,
    permissions: [PERMISSIONS.MEMBERS_READ],
    group: 'gestion',
  },
  {
    name: 'Reuniones',
    href: '/meetings',
    icon: MicrophoneIcon,
    // OPT-IN por organizacion (config.management.meetings.enabled). La pagina
    // y la API responden 403 si la org no lo tiene activado. Ademas requiere
    // permiso meetings:read.
    permissions: [PERMISSIONS.MEETINGS_READ],
    group: 'gestion',
  },

  // --- Grupo "Ajustes" (justo encima de Sistemas / al fondo si no hay Sistemas) ---
  {
    name: 'Organizaciones',
    href: '/organizations',
    icon: BuildingOfficeIcon,
    // Listado completo de TODAS las orgs — solo super_admin de plataforma.
    // El selector lateral para cambiar entre orgs propias sigue disponible
    // para cualquier user con orgs asignadas.
    permissions: ['*'],
    platform: PERMISSIONS.PLATFORM_ORGS,
    group: 'ajustes',
  },
  {
    name: 'Usuarios',
    href: '/users',
    icon: UsersIcon,
    permissions: [PERMISSIONS.USERS_READ],
    group: 'ajustes',
  },
  {
    name: 'Roles y Permisos',
    href: '/roles',
    icon: ShieldCheckIcon,
    permissions: [PERMISSIONS.USERS_READ],
    group: 'ajustes',
  },

  // --- Grupo "Sistemas" (siempre el último; Auditoria al final) ---
  {
    // Dashboard tecnico (organizaciones recientes, estado del servidor, BBDD,
    // storage). Antes vivia en '/' como inicio para todos; ahora '/' es el
    // dashboard de usuario y este queda como herramienta del super_admin.
    name: 'Dashboard Tech',
    href: '/system/dashboard-tech',
    icon: HomeIcon,
    permissions: ['*'],
    platform: PERMISSIONS.PLATFORM_SYSTEM,
    group: 'sistemas',
  },
  {
    name: 'Bases de Datos',
    href: '/databases',
    icon: CircleStackIcon,
    permissions: [PERMISSIONS.DB_READ],
    group: 'sistemas',
  },
  {
    name: 'Almacenamiento',
    href: '/storage',
    icon: CloudArrowUpIcon,
    // Backend ahora distingue dos vistas:
    //   - super_admin (*) listing global de buckets + stats globales
    //   - usuarios con MEDIA_READ en su org → /storage/my-bucket directo
    // Mostramos el item a ambos casos.
    permissions: ['*', PERMISSIONS.MEDIA_READ],
    platform: PERMISSIONS.PLATFORM_SYSTEM,
    group: 'sistemas',
  },
  {
    name: 'Sistema',
    href: '/server',
    icon: ServerIcon,
    // ['*'] = solo super_admin de plataforma. No admins de organización.
    // hasAnyPermission(['*']) devuelve true solo si el user tiene '*' en
    // sus permisos, que es lo que asigna el rol super_admin.
    permissions: ['*'],
    platform: PERMISSIONS.PLATFORM_SYSTEM,
    group: 'sistemas',
  },
  {
    name: 'Backups',
    href: '/backups',
    icon: ServerStackIcon,
    permissions: ['*'],
    platform: PERMISSIONS.PLATFORM_BACKUPS,
    group: 'sistemas',
  },
  {
    name: 'Webhooks',
    href: '/webhooks',
    icon: BellAlertIcon,
    permissions: ['*'],
    platform: PERMISSIONS.PLATFORM_APIKEYS,
    group: 'sistemas',
  },
  {
    name: 'API Keys',
    href: '/api-keys',
    icon: KeyIcon,
    permissions: ['*'],
    platform: PERMISSIONS.PLATFORM_APIKEYS,
    group: 'sistemas',
  },
  {
    name: 'Auditoria',
    href: '/audit',
    icon: ClipboardDocumentListIcon,
    permissions: [PERMISSIONS.AUDIT_READ],
    platform: PERMISSIONS.PLATFORM_SYSTEM,
    group: 'sistemas',
  },
];

const bottomNavigation: NavItem[] = [
  { name: 'Configuración', href: '/settings', icon: Cog6ToothIcon },
];

export default function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasAnyPermission, hasPlatformPermission, setPermissions } = useAuthStore();
  const {
    organizations,
    selectedOrg,
    setOrganizations,
    setSelectedOrg,
    setLoading: setOrgLoading,
  } = useOrganizationStore();
  const [hasEcommerce, setHasEcommerce] = useState(false);
  const [hasCatalog, setHasCatalog] = useState(false);
  const [hasPetAccess, setHasPetAccess] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  // Collapsible nav groups. Undefined = follow "has active child" default.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Sincroniza los permisos que lee el menu con los de la ORG seleccionada.
  //
  // Antes el auth store solo se rellenaba en el login, asi que conceder o QUITAR
  // un permiso no tenia efecto hasta cerrar sesion —y cambiar de organizacion
  // tampoco cambiaba lo que se veia—. `organizationsApi.my()` (mas abajo) trae
  // los permisos por org FRESCOS en cada arranque; aqui se vuelcan al auth store
  // cada vez que cambia la org, de modo que el menu refleja el estado real sin
  // necesidad de re-loguearse.
  useEffect(() => {
    if (selectedOrg?.permissions) setPermissions(selectedOrg.permissions);
  }, [selectedOrg, setPermissions]);

  // Las features dependen de la org SELECCIONADA (el backend las resuelve por la
  // cabecera X-Org-Id), asi que se vuelven a preguntar cada vez que cambia. Antes
  // se preguntaban una sola vez al montar: tras un login limpio aun no habia org
  // elegida y el menu salia sin Ecommerce/Catalogo, y al cambiar de org se quedaba
  // con los de la anterior. El prefetch de Ecommerce no distingue orgs, de ahi el
  // invalidate.
  const selectedOrgId = selectedOrg?._id;
  useEffect(() => {
    if (!selectedOrgId) return;
    let cancelled = false;
    useEcommerceStore.getState().invalidate();
    Promise.all([
      organizationsApi.hasEcommerce().catch(() => ({ data: { hasEcommerce: false } })),
      organizationsApi.hasCatalog().catch(() => ({ data: { hasCatalog: false } })),
    ]).then(([ecommerceRes, catalogRes]) => {
      if (cancelled) return;
      setHasEcommerce(ecommerceRes.data.hasEcommerce);
      setHasCatalog(catalogRes.data.hasCatalog);
      if (ecommerceRes.data.hasEcommerce) {
        useEcommerceStore.getState().prefetch();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedOrgId]);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const orgsRes = await organizationsApi.my();

        // Store all organizations with their permissions
        const orgsWithPermissions: OrgWithPermissions[] = orgsRes.data.organizations || [];
        setOrganizations(orgsWithPermissions);
        setOrgLoading(false);

        // Check if user has CMS access (cms:read new, or content:read alias) in any org
        const hasContentAccess = orgsWithPermissions.some(
          (org) =>
            org.permissions?.includes(PERMISSIONS.CMS_READ) ||
            org.permissions?.includes(PERMISSIONS.CONTENT_READ) ||
            org.permissions?.includes('*')
        );
        setHasPetAccess(hasContentAccess);
      } catch {
        setHasEcommerce(false);
        setHasCatalog(false);
        setHasPetAccess(false);
        setOrgLoading(false);
      }
    };
    checkAccess();
  }, [setOrganizations, setOrgLoading]);

  // Per-org module flags: hide planner/processes unless the SELECTED org has
  // the feature enabled (these are opt-in modules, off by default).
  const features = selectedOrg?.config?.features;
  // Bloque Gestion: undefined → habilitado (legacy default); false → oculto.
  const mgmt = selectedOrg?.config?.management;
  const membersOn = mgmt?.members?.enabled !== false;
  const feesOn = mgmt?.fees?.enabled !== false;
  const budgetOn = mgmt?.budget?.enabled !== false;
  // Reuniones es OPT-IN: oculto salvo que la org lo active explicitamente.
  const meetingsOn = mgmt?.meetings?.enabled === true;
  // CRM es OPT-IN por org.
  const crmOn = mgmt?.crm?.enabled === true;
  // Un TEJEDOR (org_type: 'weaver', Tejedores F4) tiene un panel REDUCIDO: solo
  // sus productos y su presencia. Sus módulos de gestión ya vienen apagados por
  // WEAVER_CONFIG, pero esto tapa además lo que se colaría por permisos: la web
  // Pet (no la tiene) y la bandeja de Colaboradores (es del lado del anfitrión,
  // y para él estaría vacía).
  const isWeaver = selectedOrg?.org_type === 'weaver';
  const filteredNavigation = navigation.filter((item) => {
    if (isWeaver && (item.href === '/pet' || item.href === '/colaboradores')) return false;
    if ((item.name === 'Ecommerce' || item.name === 'Clientes') && !hasEcommerce) return false;
    if (item.name === 'Catalogo' && !hasCatalog) return false;
    if (item.name === 'Productos' && !hasCatalog) return false;
    if (item.name === 'Proyectos' && !hasCatalog) return false;
    if (item.href === '/pet' && !hasPetAccess) return false;
    if (item.name === 'Planificador' && !features?.planner) return false;
    if (item.name === 'Procesos' && !features?.processes) return false;
    if (item.name === 'Asociados' && !membersOn) return false;
    if (item.name === 'Cuotas' && !feesOn) return false;
    if (item.name === 'Presupuesto' && !budgetOn) return false;
    if (item.name === 'Reuniones' && !meetingsOn) return false;
    if (item.group === 'crm' && !crmOn) return false;
    // Con CRM activo, la lista unificada "Contactos" sustituye al "Clientes" de ecommerce.
    if (item.name === 'Clientes' && crmOn) return false;
    // Colaboradores: dentro del grupo CRM si lo hay, suelto si no.
    // El gemelo suelto (sin `group`) solo se pinta si NO hay CRM; con CRM manda
    // el que vive dentro del grupo. Se distinguen por el grupo y no por el
    // nombre, que es lo que se muestra y debe ser igual en ambos casos.
    if (item.href === '/colaboradores' && !item.group && crmOn) return false;
    if (item.platform && hasPlatformPermission(item.platform)) return true;
    if (!item.permissions) return true;
    return hasAnyPermission(item.permissions);
  });

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const renderNavLink = (item: NavItem) => {
    const active = isActive(item.href);
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={onClose}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          active ? 'bg-vida-600 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'
        }`}
      >
        <item.icon className="w-5 h-5" />
        {item.name}
      </Link>
    );
  };

  // Render the filtered nav, collapsing contiguous same-group items into a
  // collapsible group header. A group with `flattenIfSingle` and a single
  // visible child renders that child as a normal item (no header).
  const renderNav = () => {
    const out: React.ReactNode[] = [];
    let i = 0;
    while (i < filteredNavigation.length) {
      const item = filteredNavigation[i];
      if (!item.group) {
        out.push(renderNavLink(item));
        i++;
        continue;
      }
      const gid = item.group;
      const run: NavItem[] = [];
      while (i < filteredNavigation.length && filteredNavigation[i].group === gid) {
        run.push(filteredNavigation[i]);
        i++;
      }
      const cfg = NAV_GROUPS[gid];
      if (cfg.flattenIfSingle && run.length === 1) {
        out.push(renderNavLink(run[0]));
        continue;
      }
      const hasActive = run.some((r) => isActive(r.href));
      const open = openGroups[gid] ?? hasActive;
      const GroupIcon = cfg.icon;
      out.push(
        <div key={`group-${gid}`}>
          <button
            onClick={() => setOpenGroups((p) => ({ ...p, [gid]: !open }))}
            className={`flex items-center justify-between w-full gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-800 hover:text-white ${
              hasActive ? 'text-white' : 'text-gray-300'
            }`}
          >
            <span className="flex items-center gap-3">
              <GroupIcon className="w-5 h-5" />
              {cfg.label}
            </span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${open ? '' : '-rotate-90'}`} />
          </button>
          {open && (
            <div className="mt-1 space-y-1 ml-4 pl-3 border-l border-gray-800">
              {run.map(renderNavLink)}
            </div>
          )}
        </div>
      );
    }
    return out;
  };

  return (
    <div className="flex flex-col h-full bg-gray-900 w-64">
      {/* Logo */}
      <div className="flex items-center h-16 px-6 bg-gray-950">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-vida-500 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-lg">V</span>
          </div>
          <span className="text-white font-semibold text-lg">VIDA System</span>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {renderNav()}
      </nav>

      {/* Organization selector */}
      {organizations.length > 0 && (
        <div className="px-3 py-3 border-t border-gray-800">
          <p className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Organizacion
          </p>
          <div className="relative">
            <button
              onClick={() => setShowOrgDropdown(!showOrgDropdown)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <BuildingOfficeIcon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{selectedOrg?.name || 'Seleccionar'}</span>
              </div>
              <ChevronUpDownIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
            </button>

            {showOrgDropdown && (
              <div className="absolute bottom-full left-0 right-0 mb-1 bg-gray-800 rounded-lg shadow-lg border border-gray-700 max-h-48 overflow-y-auto z-50">
                {organizations.map((org) => (
                  <button
                    key={org._id}
                    onClick={() => {
                      setSelectedOrg(org);
                      setShowOrgDropdown(false);
                      // Al cambiar de organización, ir al Dashboard de la nueva:
                      // evita quedarse en una página (p.ej. CRM) que la nueva org
                      // no tenga activada y daría error.
                      navigate('/');
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                      selectedOrg?._id === org._id
                        ? 'bg-vida-600 text-white'
                        : 'text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <BuildingOfficeIcon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{org.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom navigation */}
      <div className="px-3 py-4 border-t border-gray-800">
        {bottomNavigation.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? 'bg-vida-600 text-white'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </Link>
          );
        })}
      </div>

      {/* User section */}
      <div className="px-3 py-4 border-t border-gray-800">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {user?.profile?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.profile?.first_name || user?.email}
            </p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-3 w-full px-3 py-2 mt-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5" />
          Cerrar sesion
        </button>
        {/* Version del panel, en gris claro. Sirve para ver de un vistazo si el
            panel abierto esta desactualizado respecto a lo desplegado. Sale del
            package.json en tiempo de compilacion (vite define). */}
        <p className="px-3 mt-3 text-xs text-gray-600">v{APP_VERSION}</p>
      </div>
    </div>
  );
}
